import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { clsx } from 'clsx';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Field, { type FieldProps } from '@/components/common/Field';

export interface TertiarySelectOption {
    value: string | number | null;
    label: string;
    disabled?: boolean;
    [key: string]: any;
}

interface BaseProps extends Omit<FieldProps, 'id' | 'children'> {
    id?: string;
    options: TertiarySelectOption[];
    inputState?: 'valid' | 'invalid' | 'normal';
    renderOption?: (option: TertiarySelectOption) => ReactNode;
    name: string;
    placeholder?: string;
    disabled?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
    dropdownWidth?: number | string;
}

type ModeProps =
    | {
          multiple?: false;
          value?: TertiarySelectOption['value'];
          onChange?: (value: TertiarySelectOption['value']) => void;
      }
    | {
          multiple: true;
          value?: TertiarySelectOption['value'][];
          onChange?: (value: TertiarySelectOption['value'][]) => void;
      };

export type TertiarySelectProps = BaseProps & ModeProps;

type ListboxValue = TertiarySelectOption['value'] | TertiarySelectOption['value'][] | null;

const useListBox = (props: ModeProps) => {
    if (props.multiple) {
        return {
            multiple: true as const,
            value: props.value,
            onChange: (value: ListboxValue) => {
                if (Array.isArray(value)) {
                    props.onChange?.(value);
                }
            },
        };
    }

    return {
        multiple: false as const,
        value: props.value,
        onChange: (value: ListboxValue) => {
            if (!Array.isArray(value)) {
                props.onChange?.(value);
            }
        },
    };
};

const TertiarySelect = ({
    id,
    label,
    options,
    helpElement,
    errorElement,
    name,
    placeholder,
    disabled = false,
    required,
    searchable = false,
    searchPlaceholder = 'Search...',
    dropdownWidth,
    ...props
}: TertiarySelectProps) => {
    const elementId = useId();
    const computedId = id || elementId;
    const { t } = useTranslation('common');
    const [searchTerm, setSearchTerm] = useState('');
    const [fullscreenElement, setFullscreenElement] = useState<Element | null>(null);
    const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Track fullscreen element and button position
    useEffect(() => {
        const handleFullscreenChange = () => {
            setFullscreenElement(document.fullscreenElement);
            if (buttonRef.current) {
                setButtonRect(buttonRef.current.getBoundingClientRect());
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        setFullscreenElement(document.fullscreenElement);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    const listBoxProps = useListBox(props);

    const { value } = props;
    const selectedOptions = useMemo(() => {
        if (!value) {
            return [];
        }

        if (Array.isArray(value)) {
            return options.filter(option => value.includes(option.value));
        }

        if (!options.find(option => option.value === value)) {
            return [];
        }

        return [options.find(option => option.value === value)!];
    }, [options, value]);

    // Filter options based on search term when searchable is enabled
    const filteredOptions = useMemo(() => {
        if (!searchable || !searchTerm) {
            return options;
        }
        return options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchable, searchTerm]);

    const optionLabel =
        (selectedOptions.length === 1 && selectedOptions[0].label) ||
        (selectedOptions.length > 1 && selectedOptions.map(option => option.label).join(', ')) ||
        placeholder ||
        (props.multiple ? t('common:select.placeholderMultiple') : t('common:select.placeholderSingle'));

    // Compute dropdown style based on fullscreen and button position
    const dropdownStyle = useMemo(() => {
        if (!buttonRect) {
            return undefined;
        }

        const width = dropdownWidth !== undefined ? dropdownWidth : buttonRect.width;
        const baseStyle = { width: typeof width === 'number' ? `${width}px` : width };

        if (fullscreenElement) {
            return {
                ...baseStyle,
                left: `${buttonRect.left}px`,
                top: `${buttonRect.bottom + 8}px`,
            };
        }

        return baseStyle;
    }, [buttonRect, fullscreenElement, dropdownWidth]);

    // Update button position when dropdown opens in fullscreen
    const updateButtonRect = () => {
        if (buttonRef.current) {
            setButtonRect(buttonRef.current.getBoundingClientRect());
        }
    };

    return (
        <Listbox {...listBoxProps}>
            {({ open }) => (
                <Field
                    name={name}
                    label={label}
                    errorElement={errorElement}
                    helpElement={helpElement}
                    id={computedId}
                    required={required}
                >
                    <ListboxButton
                        ref={buttonRef}
                        disabled={disabled}
                        data-cy="select"
                        data-cy-id={computedId}
                        className="inline-flex items-center justify-start gap-1.5 rounded-md bg-white py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={updateButtonRect}
                    >
                        <span className="text-base leading-6 font-semibold text-blue-600">{optionLabel}</span>
                        {!disabled && (
                            <ChevronDown
                                aria-hidden="true"
                                className={clsx('size-4 text-blue-600 transition-transform', open && 'rotate-180')}
                            />
                        )}
                    </ListboxButton>

                    <ListboxOptions
                        anchor={fullscreenElement ? undefined : 'bottom start'}
                        className={clsx(
                            'min-w-60',
                            // position
                            fullscreenElement && buttonRect
                                ? 'fixed z-[9999] max-h-80 overflow-auto'
                                : 'absolute z-9999 mt-1 max-h-56 overflow-auto',
                            // base
                            'rounded-md bg-white text-base font-medium text-neutral-800 shadow-lg ring-1 ring-black/5 focus:outline-none',
                            // transitions
                            'data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in data-[closed]:data-[leave]:opacity-0',
                            'shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06),0px_1px_3px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-neutral-100'
                        )}
                        style={dropdownStyle}
                        data-cy="options"
                        data-cy-id={computedId}
                        transition
                    >
                        {/* Search Input - only show when searchable is true */}
                        {searchable && (
                            <div className="sticky top-0 border-b border-gray-200 bg-white px-3 py-2">
                                <div className="relative">
                                    <input
                                        type="text"
                                        className="w-full rounded-md border border-gray-300 py-1 pr-8 pl-3 text-base font-medium text-neutral-800 placeholder:text-neutral-400 focus:border-transparent focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                        placeholder={searchPlaceholder}
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        autoFocus
                                    />
                                    <Search
                                        className="absolute top-1/2 right-2 size-4 -translate-y-1/2 transform text-neutral-500"
                                        strokeWidth={2}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Options Container */}
                        <div className="flex max-h-80 flex-col items-start justify-start self-stretch overflow-y-auto p-[5px]">
                            {filteredOptions.length === 0 ? (
                                <div className="relative w-full cursor-default px-4 py-2 text-gray-500 select-none">
                                    {searchable && searchTerm ? 'No options found.' : 'No options available.'}
                                </div>
                            ) : (
                                filteredOptions.map(option => {
                                    const isSelected = selectedOptions.some(
                                        selected => selected.value === option.value
                                    );
                                    const isDisabled = option.disabled ?? false;

                                    return (
                                        <ListboxOption
                                            key={option.value}
                                            value={option.value}
                                            disabled={isDisabled}
                                            className={clsx(
                                                'inline-flex cursor-pointer items-center justify-between self-stretch rounded-md px-2 py-1.5 select-none',
                                                isSelected ? 'bg-teal-50' : 'bg-white',
                                                isSelected
                                                    ? 'text-teal-600'
                                                    : isDisabled
                                                      ? 'cursor-not-allowed text-neutral-300'
                                                      : 'text-neutral-800',
                                                'data-[focus]:bg-teal-100'
                                            )}
                                        >
                                            <span className="pr-4 text-base leading-6 font-medium">{option.label}</span>
                                            {/* Icon - only show for non-disabled options */}
                                            {!isDisabled && (
                                                <div className="relative size-4 flex-shrink-0 overflow-hidden">
                                                    {isSelected ? (
                                                        /* Current selected - checkmark outline */
                                                        <Check className="size-4 text-teal-600" strokeWidth={1.5} />
                                                    ) : (
                                                        /* Completed/answered - filled circle with checkmark */
                                                        <>
                                                            <div className="absolute top-[1.33px] left-[1.33px] size-3.5 rounded-full bg-teal-600" />
                                                            <Check
                                                                className="absolute top-[2px] left-[2px] size-3 text-white"
                                                                strokeWidth={1.5}
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </ListboxOption>
                                    );
                                })
                            )}
                        </div>
                    </ListboxOptions>
                </Field>
            )}
        </Listbox>
    );
};

export default TertiarySelect;
